import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  deleteHotelOwnerAccess,
  fetchHotelOwnerAccess,
  saveHotelOwnerAccess,
} from "../services/hotelOwnerAccessService";
import { formatDateTimeDMY12 } from "../utils/dateFormat";

const blankForm = {
  id: null,
  displayName: "",
  hotelIds: [],
  active: true,
};

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  window.prompt("Copy this value:", text);
};

const groupHotelsByCity = (hotelList) => {
  const groups = new Map();
  (hotelList || []).forEach((hotel) => {
    const city = hotel.city?.trim() || "Other";
    if (!groups.has(city)) groups.set(city, []);
    groups.get(city).push(hotel);
  });
  return Array.from(groups.entries()).sort(([left], [right]) => left.localeCompare(right));
};

// Two links to the same hotels. The owner's shows what we have paid them; the manager's does not.
const portalUrl = (item) => `${window.location.origin}/hotelportal/${item.accessKey}`;
const managerPortalUrl = (item) => (item.managerAccessKey
  ? `${window.location.origin}/hotelportal/${item.managerAccessKey}`
  : "");
// How often a link is opened (one count per browser session), so live logins stand out from
// ones nobody uses. Counting started when tracking went live - older use is not known.
const LinkUsage = ({ count, lastOpenedAt }) => (
  <div className={`small ${count > 0 ? "text-success" : "text-muted"}`}>
    {count > 0
      ? `Opened ${count} time${count === 1 ? "" : "s"} · last ${formatDateTimeDMY12(lastOpenedAt)}`
      : "Not opened yet"}
  </div>
);

const shareDetails = (item) => [
  `Owner URL: ${portalUrl(item)}`,
  managerPortalUrl(item) ? `Manager URL: ${managerPortalUrl(item)}` : "",
].filter(Boolean).join("\n");

export default function HotelOwnerAccessManager({ hotels }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const hotelCityGroups = useMemo(() => {
    const groups = new Map();
    hotels.forEach((hotel) => {
      const city = hotel.city?.trim() || "Other";
      if (!groups.has(city)) groups.set(city, []);
      groups.get(city).push({
        id: hotel.hotelId,
        label: [hotel.hotelCode, hotel.hotelName].filter(Boolean).join(" - "),
      });
    });
    return Array.from(groups.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([city, cityHotels]) => ({
        city,
        hotels: cityHotels.slice().sort((left, right) => left.label.localeCompare(right.label)),
      }));
  }, [hotels]);

  // hotelId -> name of the other active login that already has it. Each hotel gets one active
  // login; the backend refuses a second, this just says so before Save is pressed.
  const takenHotelLogins = useMemo(() => {
    const taken = new Map();
    items
      .filter((item) => item.active && item.id !== form.id)
      .forEach((item) => item.hotels.forEach((hotel) => {
        if (!taken.has(hotel.id)) taken.set(hotel.id, item.displayName);
      }));
    return taken;
  }, [items, form.id]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await fetchHotelOwnerAccess());
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load hotel owner access");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(blankForm);
    setGenerated(null);
    setShow(true);
    setExpanded(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      displayName: item.displayName,
      hotelIds: item.hotels.map((hotel) => hotel.id),
      active: item.active,
    });
    setGenerated(null);
    setShow(true);
  };

  const toggleHotel = (hotelId) => {
    setForm((current) => ({
      ...current,
      hotelIds: current.hotelIds.includes(hotelId)
        ? current.hotelIds.filter((id) => id !== hotelId)
        : [...current.hotelIds, hotelId],
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const result = await saveHotelOwnerAccess(form);
      setGenerated(result);
      await load();
      toast.success(form.id ? "Hotel owner access updated" : "Hotel owner access created");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save hotel owner access");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove access for ${item.displayName}?`)) return;
    try {
      await deleteHotelOwnerAccess(item.id);
      await load();
      toast.success("Hotel owner access removed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to remove hotel owner access");
    }
  };

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header
        className="d-flex flex-wrap justify-content-between align-items-center gap-2"
        role="button"
        onClick={() => setExpanded((current) => !current)}
      >
        <div>
          <div className="fw-semibold">
            <i className={`bi ${expanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`} aria-hidden="true" />
            Hotel Owner Access
          </div>
          <div className="small text-muted">Create separate login URLs and assign one or multiple hotels.</div>
        </div>
        <Button size="sm" onClick={(event) => { event.stopPropagation(); openNew(); }}>Add Hotel Login</Button>
      </Card.Header>
      {expanded && (
        <Card.Body className="p-0">
          {loading && <div className="p-3"><Spinner animation="border" size="sm" /> Loading access profiles...</div>}
          {!loading && items.length === 0 && <Alert variant="light" className="m-3">No hotel owner access has been created.</Alert>}
          {!loading && items.length > 0 && (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr><th style={{ width: 44 }}>#</th>
                  <th>Owner / Group</th>
                  <th>Assigned Hotels</th>
                  <th>Status</th>
                  <th>Portal Links</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id}><td className="small fw-semibold">{index + 1}</td>
                    <td className="fw-semibold">{item.displayName}</td>
                    <td>{groupHotelsByCity(item.hotels).map(([city, cityHotels]) => (
                      <div key={city}>
                        <span className="text-muted small">{city}:</span> {cityHotels.map((hotel) => hotel.hotelCode || hotel.hotelName).join(", ")}
                      </div>
                    ))}
                    {(item.sharedHotels || []).length > 0 && (
                      <div className="small text-danger mt-1">
                        <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
                        Duplicate login: {item.sharedHotels.join(", ")}. Keep one and remove the other.
                      </div>
                    )}</td>
                    <td><Badge bg={item.active ? "success" : "secondary"}>{item.active ? "Active" : "Inactive"}</Badge></td>
                    <td className="text-nowrap">
                      <div className="d-flex flex-column gap-1 align-items-start">
                        <Button size="sm" variant="outline-secondary" onClick={() => copyText(portalUrl(item))}>
                          Copy Owner URL
                        </Button>
                        <LinkUsage count={item.ownerOpenCount || 0} lastOpenedAt={item.ownerLastOpenedAt} />
                        {managerPortalUrl(item) && (
                          <>
                            <Button size="sm" variant="outline-secondary" onClick={() => copyText(managerPortalUrl(item))}>
                              Copy Manager URL
                            </Button>
                            <LinkUsage count={item.managerOpenCount || 0} lastOpenedAt={item.managerLastOpenedAt} />
                          </>
                        )}
                      </div>
                    </td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(item)}>Edit</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => remove(item)}>Remove</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      )}

      <Modal show={show} onHide={() => setShow(false)} size="lg" centered className="hotel-owner-access-modal">
        <Form onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title>{form.id ? "Edit Hotel Owner Access" : "Add Hotel Owner Access"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Label>Owner / Group Name</Form.Label>
                <Form.Control required value={form.displayName} onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))} />
              </Col>
              <Col xs={12}>
                <Form.Label>Assign Hotels</Form.Label>
                <div className="border rounded p-3 d-flex flex-column gap-3" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {hotelCityGroups.map((group) => (
                    <div key={group.city}>
                      <div className="text-uppercase text-muted small fw-semibold mb-1">{group.city}</div>
                      <div className="d-flex flex-column gap-2">
                        {group.hotels.map((hotel) => {
                          const takenBy = form.active ? takenHotelLogins.get(hotel.id) : null;
                          const checked = form.hotelIds.includes(hotel.id);
                          return (
                            <Form.Check
                              key={hotel.id}
                              type="checkbox"
                              id={`owner-hotel-${hotel.id}`}
                              label={takenBy
                                ? <span>{hotel.label} <span className="text-muted small">- already on {takenBy}</span></span>
                                : hotel.label}
                              checked={checked}
                              // Still untickable when already ticked, so an old duplicate can be cleaned up here.
                              disabled={Boolean(takenBy) && !checked}
                              onChange={() => toggleHotel(hotel.id)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
              {form.id && (
                <>
                  <Col md={12}>
                    <Form.Check type="switch" label="Access active" checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} />
                  </Col>
                </>
              )}
              {generated && (
                <Col xs={12}>
                  <Alert variant="success" className="mb-0">
                    <div className="fw-semibold mb-2">Share these portal links securely</div>
                    <Button size="sm" variant="success" className="mb-2" onClick={() => copyText(shareDetails(generated))}>Copy Both URLs</Button>
                    <div className="mb-1">
                      <span className="fw-semibold">Owner</span> (sees payments): <code>{portalUrl(generated)}</code>{" "}
                      <Button size="sm" variant="outline-success" onClick={() => copyText(portalUrl(generated))}>Copy</Button>
                    </div>
                    {managerPortalUrl(generated) && (
                      <div className="mb-1">
                        <span className="fw-semibold">Manager</span> (no payments): <code>{managerPortalUrl(generated)}</code>{" "}
                        <Button size="sm" variant="outline-success" onClick={() => copyText(managerPortalUrl(generated))}>Copy</Button>
                      </div>
                    )}
                  </Alert>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Close</Button>
            <Button type="submit" disabled={saving || form.hotelIds.length === 0}>
              {saving ? <Spinner animation="border" size="sm" /> : form.id ? "Save Changes" : "Save Access"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Card>
  );
}
